import React, { useCallback, useEffect } from 'react';
import { Box, FormHelperText, useMediaQuery } from '@mui/material';
import { styles } from '../../../forms/BusinessRelations/styles';
import LicenseTableRow from './LicenseTableRow';
import { TableHeader } from '~/components/ListCardTable/TableHeader';
import { theme } from '~/theme';
import { FormYesNoQuestion } from '~/components/FormComponents/FormYesNoQuestion';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { errorMessages } from '~/constants/errorMessages.ts';
import {
  EMPTY_LICENSE,
  LICENSE_HEDER_HEIGHT,
  LICENSE_MOBILE_BUTTONS_HEIGHT,
  LICENSE_ROW_HEIGHT,
  licenseTableColumns,
} from '~/modules/identification/constants';
import { TLicense } from '~/modules/identification/types';
import { useVirtualizer } from '@tanstack/react-virtual';
import { SystemStyleObject, Theme } from '@mui/system';

interface ILicenseExistence {
  isLicensedFieldName: string;
}

export const LicenseExistence = ({ isLicensedFieldName }: ILicenseExistence) => {
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const {
    control,
    clearErrors,
    formState: { errors, isSubmitted },
  } = useFormContext<{ licenses: TLicense[]; [isLicensedFieldName]: boolean }>();

  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'licenses',
  });

  const isLicensed = useWatch({
    control: control,
    name: isLicensedFieldName,
  });
  const parentRef = React.useRef<HTMLDivElement | null>(null);
  const rowVirtualizer = useVirtualizer({
    count: fields.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () =>
      isMobile ? LICENSE_ROW_HEIGHT * 5 + LICENSE_MOBILE_BUTTONS_HEIGHT : LICENSE_ROW_HEIGHT,
    overscan: 5,
  });

  const maxVisibleRows = isMobile ? 1 : 5;
  const visibleRows = Math.min(fields.length, maxVisibleRows);
  const rowsHeight = LICENSE_ROW_HEIGHT * visibleRows * (isMobile ? 5 : 1);
  const extraHeight = isMobile ? LICENSE_MOBILE_BUTTONS_HEIGHT : LICENSE_HEDER_HEIGHT;
  const containerHeight = rowsHeight + (fields.length > maxVisibleRows ? extraHeight : extraHeight);

  useEffect(() => {
    rowVirtualizer.measure();
  }, [isMobile]);

  const handleChange = (isChecked: boolean) => {
    if (isChecked) {
      replace([{ ...EMPTY_LICENSE }]);
    } else {
      replace([]);
      clearErrors('licenses');
    }
  };

  const handleAppend = useCallback(() => {
    const scrollElement = parentRef.current;
    const previousHeight = scrollElement?.scrollHeight ?? 0;
    append({ ...EMPTY_LICENSE });
    requestAnimationFrame(() => {
      if (!scrollElement) return;
      const delta = scrollElement.scrollHeight - previousHeight;
      scrollElement.scrollTop += delta;
    });
    clearErrors('licenses');
  }, [append, clearErrors]);

  const handleRemove = (id?: string) => {
    const index = fields.findIndex((f) => f.id === id);
    remove(index);
  };

  return (
    <Box sx={styles.container}>
      <FormYesNoQuestion
        question="Наявність ліцензій (дозволів) на право здійснення певних операцій (діяльності)"
        name={isLicensedFieldName}
        onChange={handleChange}
      />
      {isLicensed && (
        <>
          <Box
            sx={[styles.table as SystemStyleObject<Theme>, { height: containerHeight }]}
            data-name="licenses"
            ref={parentRef}
          >
            {!isMobile && <TableHeader columns={licenseTableColumns} showActions />}
            <Box
              sx={{ height: rowVirtualizer.getTotalSize(), width: '100%', position: 'relative' }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualItem) => (
                <Box
                  key={virtualItem.key}
                  data-index={virtualItem.index}
                  ref={rowVirtualizer.measureElement}
                  sx={styles.rowWrapper(virtualItem.start)}
                >
                  <LicenseTableRow
                    key={fields[virtualItem.index]?.id}
                    virtualItem={virtualItem}
                    showRemove={fields.length > 1}
                    append={handleAppend}
                    remove={() => handleRemove(fields[virtualItem.index]?.id)}
                    isLastItem={fields.length - 1 === virtualItem.index}
                  />
                </Box>
              ))}
            </Box>
          </Box>
        </>
      )}
      <FormHelperText sx={{ top: '100%' }}>
        {isSubmitted && errors.licenses ? errorMessages.FILL_ALL_FIELDS : ''}
      </FormHelperText>
    </Box>
  );
};

export default LicenseExistence;
